import { useEffect, useContext } from "react";
import { useSearchboxStore, useActions } from "../../store/searchBoxStore.js";
import { actions as searchActions } from "../../store/actions.js";
import { SearchBoxContext } from "../SearchBoxContext/index.js";
const isDateRangeFetched = (dateRange, fetchedRanges, departureDate) => {
    const [requestStart, requestEnd] = dateRange;
    const fullyCovered = fetchedRanges.some((range) => {
        const rangeMatches = range.start <= requestStart && range.end >= requestEnd;
        if (departureDate) {
            return rangeMatches && range.departureDate === departureDate;
        }
        return rangeMatches && !range.departureDate;
    });
    if (fullyCovered) {
        return true;
    }
    const requestStartDate = new Date(requestStart);
    const requestEndDate = new Date(requestEnd);
    const requestDuration = requestEndDate.getTime() - requestStartDate.getTime();
    let overlappingDuration = 0;
    fetchedRanges.forEach((range) => {
        if (departureDate && range.departureDate !== departureDate) {
            return;
        }
        if (!departureDate && range.departureDate) {
            return;
        }
        const rangeStart = new Date(range.start);
        const rangeEnd = new Date(range.end);
        const overlapStart = new Date(Math.max(requestStartDate.getTime(), rangeStart.getTime()));
        const overlapEnd = new Date(Math.min(requestEndDate.getTime(), rangeEnd.getTime()));
        if (overlapStart < overlapEnd) {
            overlappingDuration += overlapEnd.getTime() - overlapStart.getTime();
        }
    });
    const overlapPercentage = overlappingDuration / requestDuration;
    return overlapPercentage > 0.66;
};
export const useFetchFareCalendar = ({ departureDateRange, returnDateRange, enabled = true, segmentIndex = 0 } = {}) => {
    const state = useSearchboxStore();
    const fareCalendar = state.searchbox.fareCalendar;
    const actions = useActions(searchActions);
    const segment = state.searchbox.searchSegments[segmentIndex];
    const { autoCompleteBaseURL } = useContext(SearchBoxContext);
    const originLocation = segment?.from?.find((loc) => loc.type === "CITY" || loc.type === "AIRPORT");
    const destinationLocation = segment?.to?.find((loc) => loc.type === "CITY" || loc.type === "AIRPORT");
    const originCode = originLocation?.code;
    const destinationCode = destinationLocation?.code;
    const departureDateRangeStart = departureDateRange?.[0];
    const departureDateRangeEnd = departureDateRange?.[1];
    const returnDateRangeStart = returnDateRange?.[0];
    const returnDateRangeEnd = returnDateRange?.[1];
    const tripType = state.searchbox.type;
    const cabinClass = state.searchbox.cabinClass;
    const isDirect = state.searchbox.isDirect;
    const baseUrl = autoCompleteBaseURL?.url;
    useEffect(() => {
        if (!enabled || !departureDateRangeStart || !departureDateRangeEnd || !originLocation || !destinationLocation) {
            return;
        }
        const rangeToCheck = returnDateRange
            ? [returnDateRangeStart, returnDateRangeEnd]
            : [departureDateRangeStart, departureDateRangeEnd];
        const departureDateForCache = returnDateRange ? departureDateRangeStart : undefined;
        const alreadyFetched = isDateRangeFetched(rangeToCheck, fareCalendar.fetchedRanges, departureDateForCache);
        if (alreadyFetched) {
            return;
        }
        actions.fareCalendarStart();
        const payload = {
            origin: {
                cityIata: originLocation.type === "CITY" ? originLocation.code : undefined,
                airportIata: originLocation.type === "AIRPORT" ? originLocation.code : undefined
            },
            destination: {
                cityIata: destinationLocation.type === "CITY" ? destinationLocation.code : undefined,
                airportIata: destinationLocation.type === "AIRPORT" ? destinationLocation.code : undefined
            },
            departureDateRange: [departureDateRangeStart, departureDateRangeEnd],
            ...(returnDateRange && {
                returnDateRange: [returnDateRangeStart, returnDateRangeEnd]
            }),
            tripType: tripType === "ROUNDTRIP" ? "ROUND_TRIP" : "ONE_WAY",
            filters: {
                cabinClass: cabinClass,
                maxNumberOfStops: isDirect ? 0 : undefined
            }
        };
        const apiUrl = baseUrl ? `${baseUrl}/api/flyAnywhere/fareCalendar` : "/api/flyAnywhere/fareCalendar";
        fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload),
            credentials: "include"
        })
            .then((response) => response.json())
            .then((data) => {
            actions.fareCalendarSuccess(data.fares || [], rangeToCheck, departureDateForCache);
        })
            .catch((error) => {
            actions.fareCalendarError(error.message || "Failed to fetch fare calendar data");
        });
    }, [
        departureDateRangeStart,
        departureDateRangeEnd,
        returnDateRangeStart,
        returnDateRangeEnd,
        enabled,
        originCode,
        destinationCode,
        tripType,
        cabinClass,
        isDirect,
        baseUrl
    ]);
    return {
        data: fareCalendar.data,
        loading: fareCalendar.loading,
        error: fareCalendar.error
    };
};
